/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useEffect, useId, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { AccountNameAutocomplete } from "../utils/datasources/AutoComplete";
import {
  EmailFollowOnRow,
  NewAddressForm,
  OfficeFollowOnRow,
} from "./AccountFollowOnRows";
import { CrmAccount, preferredCandidate } from "./helpers/actions";
import { getVerification } from "./helpers/emailVerification";
import { CandidateValueGroup, LedgerField } from "./helpers/normalize";
import {
  AccountEmailTarget,
  AccountFollowOns,
  describeAddress,
  useAccountFollowOns,
} from "./helpers/useAccountFollowOns";
import classes from "./css/recordReview.module.css";

export type { AccountFollowOns };

/*
 * Company is the one field on this surface that cannot be approved as text.
 *
 * The comparison table Monitor ships with has always known this: a detected
 * employer is a NAME, and what the CRM stores is a link to an account record.
 * Writing the name alone leaves the contact unlinked to the account everyone
 * else works from - correct on screen, useless to the business - so approving
 * a company means choosing the account it refers to.
 *
 * This is that resolution, ported into the ledger's grammar: the picker sits
 * with the proposed value it resolves, and the write closes the decision
 * beneath everything it sends. Choosing an account also tells us
 * things the account knows about the contact - a work email domain, the
 * offices it has - so those arrive here as further proposals rather than
 * landing silently with the company.
 *
 * Those proposals are part of the company decision, not separate ones. A job
 * move is one change with up to three parts, and as separate writes the
 * follow-ons either went before the company (the only time their controls
 * existed) or not at all (the picker leaves with the row once the company
 * lands). So each arrives here ticked and the steward opts out of any they
 * don't want, and the one write sends the company first and the chosen
 * follow-ons only once it has landed.
 *
 * It serves both ways a company gets set on this surface. With a pending value
 * behind it, it resolves that proposal. Opened from a pencil with nothing
 * pending, it IS the ad-hoc company edit - the one field whose overwrite
 * cannot be a text box, which is why the text pencil has always refused it.
 */

export interface CompanyResolutionActions {
  integratedSystemId: number;
  recordId: number;
  /**
   * Where a work email derived from the account would land. Absent when
   * nothing can receive it - then the suggestion is withheld rather than
   * shown with no way to act on it.
   */
  emailTarget?: AccountEmailTarget;
  /**
   * Writes the company and whichever follow-ons the steward opted into. The
   * group is absent for an ad-hoc edit: nothing is being resolved.
   */
  onApproveAccount: (
    field: LedgerField,
    account: CrmAccount,
    group: CandidateValueGroup | undefined,
    followOns: AccountFollowOns
  ) => void;
  /** False when ad-hoc writes are unavailable (no explain payload loaded). */
  canWriteAccountEmail: boolean;
  /** Where the write lands, named: "HubSpot", "InterAction", or "your CRM". */
  crmName: string;
}

/**
 * Whether approving this proposed value has to go through an account.
 *
 * Two cases need it. A Monitor detection is a name scraped off a profile and
 * has no account behind it. And a Cirrom change with no value at all is the
 * "company field is not populated" finding, which exists precisely to ask for
 * one. A Cirrom change that carries a value was resolved upstream, so it keeps
 * the plain approve it has today.
 */
export const requiresAccountResolution = (
  field: LedgerField,
  group: CandidateValueGroup
): boolean => {
  if (!isAccountResolvedField(field.fieldName)) return false;
  return preferredCandidate(group).source === "monitor" || !group.value;
};

/**
 * Fields whose own value is a link to another record, so every edit of them -
 * proposed or ad-hoc - goes through a picker rather than a text box.
 */
export const isAccountResolvedField = (fieldName: string): boolean =>
  fieldName === "company";

export const CompanyResolution = (props: {
  field: LedgerField;
  /** The proposed value being resolved; absent when this is an ad-hoc edit. */
  group?: CandidateValueGroup;
  disabled: boolean;
  actions: CompanyResolutionActions;
  /**
   * Closes the editor - both ways out of it, backing off and writing. Only
   * the pencil-opened edit has one; the resolution of a pending value lives
   * with that value and goes when the row does.
   */
  onClose?: () => void;
}) => {
  const { field, group, actions } = props;
  const [account, setAccount] = useState<CrmAccount | undefined>();
  /*
   * An account email with nowhere to go - no target field, or no ad-hoc write
   * path - would be a suggestion the steward cannot take, so it is withheld.
   */
  const emailTarget = actions.canWriteAccountEmail
    ? actions.emailTarget
    : undefined;
  const followOns = useAccountFollowOns({
    account,
    integratedSystemId: actions.integratedSystemId,
    recordId: actions.recordId,
    emailTarget,
    crmName: actions.crmName,
  });
  const { email, office, newAddress } = followOns;
  /*
   * An open new-address form holds the panel: nothing else changes or writes
   * until it is created or dismissed.
   */
  const locked = newAddress.locked;
  /*
   * The form's Discard and Create leave with the form, so focus would fall to
   * the modal. It goes back to where the form was opened from instead: the
   * office picker when there is one (holding the created office, if one was
   * made), otherwise the Add new address button.
   */
  const officeInputRef = useRef<HTMLInputElement>(null);
  const addNewButtonRef = useRef<HTMLButtonElement>(null);
  const formWasOpen = useRef(false);
  useEffect(() => {
    if (formWasOpen.current && !newAddress.open) {
      (officeInputRef.current ?? addNewButtonRef.current)?.focus();
    }
    formWasOpen.current = newAddress.open;
  }, [newAddress.open]);
  /** The lookup has answered, either way: the follow-ons can be laid out. */
  const settled = !followOns.loading && (followOns.loaded || followOns.failed);

  /*
   * What the write sends, in the order it sends it - and what it leaves
   * behind. Shown above the button and read as its description, so sighted
   * and screen-reader stewards get the same scope before the one click that
   * can't be undone, without hovering for it.
   *
   * The office is named by its address: the picker truncates it, and "link
   * the office" asked the steward to trust a choice they couldn't read.
   * Exclusions are named too, because a refused email quietly unticked read
   * as though it would still go.
   */
  const crmName = actions.crmName;
  const followOnWords = [
    followOns.chosen.email ? `write ${emailTarget!.label}` : null,
    followOns.chosen.address && office.selected
      ? `link the office at ${describeAddress(office.selected)}`
      : null,
  ].filter(Boolean) as string[];
  const verification = email.blocked ? getVerification(email.typed) : undefined;
  const blockPhrase =
    verification?.phase === "verified"
      ? verification.verdict.blockPhrase
      : undefined;
  const leftOut = [
    // An email the record already holds isn't left out: there was nothing to send.
    email.suggested && !followOns.chosen.email && email.writable
      ? blockPhrase
        ? `${
            emailTarget!.label
          } isn't written: Allegrow reports ${blockPhrase}.`
        : `${emailTarget!.label} isn't written.`
      : null,
    office.addresses.length > 0 && !followOns.chosen.address
      ? "No office is linked."
      : null,
  ].filter(Boolean) as string[];
  const writeScope = !account
    ? "Pick an account first."
    : [
        followOnWords.length
          ? `Link this contact to ${
              account.name
            } in ${crmName}, then ${followOnWords.join(
              " and "
            )} once that has landed.`
          : `Link this contact to ${account.name} in ${crmName}.`,
        ...leftOut,
      ].join(" ");
  const writeDescriptionId = useId();
  // Once the lookup has answered, the scope is settled enough to state.
  const showScope = Boolean(account) && settled && !locked;
  /*
   * The button says how much it writes, because a tick beside the Company
   * field read as "apply the company" while it could send three changes.
   */
  const changeCount = 1 + followOnWords.length;
  const writeLabel =
    changeCount === 1 ? "Write company" : `Write ${changeCount} changes`;

  return (
    <div className={classes.companyResolution}>
      {/*
       * Only the resolve case explains itself: that row is refusing an
       * approve the steward expected to have. Opened from a pencil, the
       * field's own label and the picker already say what this is, and the
       * line cost a row in a column that is short of them.
       */}
      {group && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Link this contact to an account, not a name: pick the one this refers
          to.
        </Typography>
      )}
      <div className={classes.companyResolutionPicker}>
        <div className={classes.companyResolutionField}>
          <AccountNameAutocomplete
            integratedSystemId={actions.integratedSystemId}
            disabled={props.disabled || locked}
            /*
             * Opened from a pencil, the picker is the only thing to do next,
             * so it takes focus as the text editors do. A pending value's
             * picker shares the modal with other decisions and waits.
             */
            autoFocus={Boolean(props.onClose)}
            ariaLabel={`Account to link for ${field.label}`}
            // Leaving it empty is a choice - Cancel - not a missing input.
            required={false}
            onSelectionChanged={setAccount}
            // A cleared picker is no account: the write must not keep the
            // one it showed a moment ago.
            onSelectionCleared={() => setAccount(undefined)}
          />
        </div>
      </div>

      {account && (
        /*
         * Consequences of the choice, kept visibly downstream of it: an
         * indent rule rather than a panel, because these are still part of
         * the company decision and must not read as a second field.
         *
         * The rule and its first line arrive with the pick, the lookup
         * filling them in place: the block appearing only once the lookup
         * answered pushed the write down mid-glance, every record.
         */
        <Box
          className={
            settled
              ? classes.accountFollowOns
              : `${classes.accountFollowOns} ${classes.accountFollowOnsPending}`
          }
          sx={{ borderColor: "divider" }}
        >
          {!settled ? (
            <div className={classes.accountFollowOnRow} role="status">
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {`Looking up ${account.name}'s email and offices…`}
              </Typography>
            </div>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {followOns.failed
                ? `Couldn't load ${account.name}'s email and offices. The company still writes on its own.`
                : followOnWords.length
                ? `Also update from ${account.name}`
                : // Not "Also update" over choices that won't go
                  `Nothing else from ${account.name} is written`}
            </Typography>
          )}

          {settled && (
            <>
              {email.note && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {email.note}
                </Typography>
              )}

              {email.suggested && (
                <EmailFollowOnRow
                  email={email}
                  accountName={account.name}
                  targetLabel={emailTarget!.label}
                  verification={{
                    crmContactId: emailTarget!.crmContactId,
                    integratedSystemId: actions.integratedSystemId,
                  }}
                  disabled={props.disabled || locked}
                />
              )}

              {followOns.loaded && office.addresses.length === 0 && (
                <div className={classes.accountFollowOnRow}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {`${account.name} has no offices on record to link.`}
                  </Typography>
                  {newAddress.available && (
                    <Button
                      size="small"
                      ref={addNewButtonRef}
                      disabled={props.disabled || locked}
                      onClick={newAddress.openForm}
                      sx={{ flexShrink: 0 }}
                    >
                      Add new address
                    </Button>
                  )}
                </div>
              )}

              {office.addresses.length > 0 && (
                <OfficeFollowOnRow
                  office={office}
                  accountName={account.name}
                  disabled={props.disabled || locked}
                  inputRef={officeInputRef}
                  onAddNew={
                    newAddress.available ? newAddress.openForm : undefined
                  }
                />
              )}

              {newAddress.available && newAddress.open && (
                <NewAddressForm
                  newAddress={newAddress}
                  accountName={account.name}
                  crmName={crmName}
                  underOffice={office.addresses.length > 0}
                />
              )}

              {newAddress.note && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {newAddress.note}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/*
       * The commit closes the decision instead of sitting beside its first
       * field: the follow-ons above change what it sends, so it comes after
       * them - on screen and in tab order. Outlined, not contained: the
       * modal's one contained button is Approve all.
       *
       * An open new-address form takes the commit for itself: it carries
       * its own Create, and a disabled Write beneath it read as a second,
       * competing way to finish. Cancel stays - it is the way out of
       * everything, the form included.
       */}
      {showScope && (
        <Typography
          id={writeDescriptionId}
          variant="body2"
          className={classes.companyResolutionScope}
          // The one line here that says what happens: primary, not the
          // secondary grey of the notes above it.
          sx={{ color: "text.primary" }}
        >
          {writeScope}
        </Typography>
      )}
      <div className={classes.companyResolutionFooter}>
        {props.onClose && (
          // describeChild: the tooltip explains Cancel, it doesn't rename it -
          // a screen reader should hear the word on the button.
          <Tooltip
            describeChild
            title={`Stop editing. Nothing is written to ${crmName}.`}
          >
            <Button
              size="small"
              // Discards an open new-address form with the rest; only a
              // create already on its way can't be walked away from.
              disabled={newAddress.creating}
              onClick={props.onClose}
            >
              Cancel
            </Button>
          </Tooltip>
        )}
        {!locked && (
          // The visible scope line says it once an account is picked
          <Tooltip title={account ? "" : writeScope}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={props.disabled || !account}
                aria-describedby={writeDescriptionId}
                onClick={() => {
                  if (!account) return;
                  actions.onApproveAccount(
                    field,
                    account,
                    group,
                    followOns.chosen
                  );
                  // An ad-hoc edit is finished; a resolved proposal keeps its
                  // picker until the refetch takes the row away.
                  props.onClose?.();
                }}
              >
                {writeLabel}
              </Button>
            </span>
          </Tooltip>
        )}
        {!locked && !showScope && (
          <span id={writeDescriptionId} className={classes.visuallyHidden}>
            {writeScope}
          </span>
        )}
      </div>
    </div>
  );
};
